import { Interview, MeetingTranscriptionItem } from './api';

interface OutgoingEventEnvelope<T = any> {
  type: string;
  entity: string;
  version: number;
  payload: T;
}

// Build WebSocket URL based on backend URL env, keeping nginx paths unchanged.
// HTTP  -> ws
// HTTPS -> wss
// Final URL: <base-without-/api>/api/ws?token=...
const getWebSocketURL = (token: string) => {
  const raw = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
  let clean = raw.replace(/[}\s/]+$/, '').replace(/\/+$/, '');
  // Remove /api suffix if present
  clean = clean.replace(/\/api$/, '');

  let url: URL;
  try {
    url = new URL(clean);
  } catch {
    // Fallback for bare host:port without protocol
    url = new URL(`http://${clean}`);
  }

  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  // Ensure we don't double up slashes
  const base = url.toString().replace(/\/+$/, '');
  const wsUrl = `${base}/api/ws?token=${encodeURIComponent(token)}`;
  return wsUrl;
};

class WebSocketService {
  private socket: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelayMs = 1000;

  // Ping/pong heartbeat
  private pingInterval: NodeJS.Timeout | null = null;
  private readonly PING_INTERVAL_MS = 25000; // Send ping every 25 seconds (before server's 30s timeout)

  private lastToken: string | null = null;
  private onNewInterviewCb: ((interview: Interview) => void) | null = null;
  private onInterviewUpdateCb: ((interview: Interview) => void) | null = null;
  private onInterviewDeletedCb: ((interviewId: string) => void) | null = null;
  private onMessageStreamingCb: ((event: {
    type: 'started' | 'delta' | 'complete' | 'error' | 'cancelled';
    interviewId: string;
    messageId: string;
    delta?: string;
    content?: string;
    error?: string;
  }) => void) | null = null;
  private onNewTranscriptCb: ((transcript: MeetingTranscriptionItem) => void) | null = null;
  private onTranscriptUpdateCb: ((transcript: MeetingTranscriptionItem) => void) | null = null;

  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  connect(
    token: string,
    onNewInterview: (interview: Interview) => void,
    onInterviewUpdate: (interview: Interview) => void,
    onInterviewDeleted?: (interviewId: string) => void,
    onMessageStreaming?: (event: {
      type: 'started' | 'delta' | 'complete' | 'error' | 'cancelled';
      interviewId: string;
      messageId: string;
      delta?: string;
      content?: string;
      error?: string;
    }) => void,
    onNewTranscript?: (transcript: MeetingTranscriptionItem) => void,
    onTranscriptUpdate?: (transcript: MeetingTranscriptionItem) => void,
  ) {
    if (!token) {
      console.error('❌ Cannot connect WebSocket: No token provided');
      return;
    }

    // If token changed, disconnect old connection first
    if (this.lastToken && this.lastToken !== token && this.socket) {
      console.log('Token changed, disconnecting old WebSocket connection...');
      this.disconnect();
    }

    // Update callbacks
    this.lastToken = token;
    this.onNewInterviewCb = onNewInterview;
    this.onInterviewUpdateCb = onInterviewUpdate;
    if (onInterviewDeleted) {
      this.onInterviewDeletedCb = onInterviewDeleted;
    }
    if (onMessageStreaming) {
      this.onMessageStreamingCb = onMessageStreaming;
    }
    if (onNewTranscript) {
      this.onNewTranscriptCb = onNewTranscript;
    }
    if (onTranscriptUpdate) {
      this.onTranscriptUpdateCb = onTranscriptUpdate;
    }

    // If already connected or connecting, just update callbacks (don't reconnect)
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) {
      console.log('WebSocket already connected, updating callbacks only...');
      return;
    }

    const wsUrl = getWebSocketURL(token);
    console.log('🔌 Connecting WebSocket to', wsUrl);

    this.socket = new WebSocket(wsUrl);

    this.socket.onopen = () => {
      this.reconnectAttempts = 0;
      console.log('✅ WebSocket connected to', wsUrl);
      // Start ping/pong heartbeat
      this.startHeartbeat();
    };

    this.socket.onclose = (event) => {
      // Stop heartbeat
      this.stopHeartbeat();
      
      console.log('❌ WebSocket disconnected:', event.code, event.reason || '(no reason)');
      // If token is invalid, backend will close with 4001 – treat as auth issue and stop reconnecting
      if (event.code === 4001) {
        console.error('   WebSocket closed due to authentication error. Clearing token and user.');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        return;
      }

      // Attempt reconnect if we still have a token and have not exceeded attempts
      if (this.lastToken && this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts += 1;
        const delay = this.reconnectDelayMs * this.reconnectAttempts;
        console.log(`   Attempting WebSocket reconnect #${this.reconnectAttempts} in ${delay}ms...`);
        setTimeout(() => {
          if (this.lastToken && this.onNewInterviewCb && this.onInterviewUpdateCb) {
            this.connect(
              this.lastToken,
              this.onNewInterviewCb,
              this.onInterviewUpdateCb,
              this.onInterviewDeletedCb || undefined,
              this.onMessageStreamingCb || undefined,
              this.onNewTranscriptCb || undefined,
              this.onTranscriptUpdateCb || undefined
            );
          }
        }, delay);
      }
    };

    this.socket.onerror = (event) => {
      console.error('❌ WebSocket connection error:', event);
    };

    this.socket.onmessage = (event) => {
      try {
        // Handle pong response (JSON-based ping/pong)
        const data = JSON.parse(event.data);
        if (data.type === 'pong') {
          // Pong received, connection is alive
          return;
        }

        const envelope = data as OutgoingEventEnvelope;
        if (!envelope || typeof envelope.type !== 'string') {
          return;
        }

        if (envelope.entity === 'interview' && envelope.version === 1) {
          const interviewPayload = envelope.payload as any;
          switch (envelope.type) {
            case 'interview.created':
              if (this.onNewInterviewCb && interviewPayload) {
                console.log('📨 New interview received via WebSocket:', interviewPayload.id);
                this.onNewInterviewCb(interviewPayload as Interview);
              }
              break;
            case 'interview.updated':
              if (this.onInterviewUpdateCb && interviewPayload) {
                console.log('🔄 Interview update received via WebSocket:', interviewPayload.id);
                this.onInterviewUpdateCb(interviewPayload as Interview);
              }
              break;
            case 'interview.deleted':
              if (this.onInterviewDeletedCb && interviewPayload && interviewPayload.id) {
                console.log('🗑️ Interview deleted via WebSocket:', interviewPayload.id);
                this.onInterviewDeletedCb(interviewPayload.id as string);
              }
              break;
            default:
              // Unknown interview event type - ignore for now
              break;
          }
        } else if (envelope.entity === 'message-streaming' && envelope.version === 1) {
          const streamingPayload = envelope.payload as any;
          if (this.onMessageStreamingCb && streamingPayload) {
            switch (envelope.type) {
              case 'message-streaming.started':
                this.onMessageStreamingCb({
                  type: 'started',
                  interviewId: streamingPayload.interviewId,
                  messageId: streamingPayload.messageId,
                });
                break;
              case 'message-streaming.delta':
                this.onMessageStreamingCb({
                  type: 'delta',
                  interviewId: streamingPayload.interviewId,
                  messageId: streamingPayload.messageId,
                  delta: streamingPayload.delta,
                });
                break;
              case 'message-streaming.complete':
                this.onMessageStreamingCb({
                  type: 'complete',
                  interviewId: streamingPayload.interviewId,
                  messageId: streamingPayload.messageId,
                  content: streamingPayload.content,
                });
                break;
              case 'message-streaming.error':
                this.onMessageStreamingCb({
                  type: 'error',
                  interviewId: streamingPayload.interviewId,
                  messageId: streamingPayload.messageId,
                  error: streamingPayload.error,
                });
                break;
              case 'message-streaming.cancelled':
                this.onMessageStreamingCb({
                  type: 'cancelled',
                  interviewId: streamingPayload.interviewId,
                  messageId: streamingPayload.messageId,
                });
                break;
            }
          }
        } else if (envelope.entity === 'transcript' && envelope.version === 1) {
          const transcriptPayload = envelope.payload as any;
          switch (envelope.type) {
            case 'transcript.created':
              if (this.onNewTranscriptCb && transcriptPayload) {
                console.log('📨 New transcript received via WebSocket:', transcriptPayload.id);
                this.onNewTranscriptCb(transcriptPayload as MeetingTranscriptionItem);
              }
              break;
            case 'transcript.updated':
              if (this.onTranscriptUpdateCb && transcriptPayload) {
                console.log('🔄 Transcript update received via WebSocket:', transcriptPayload.id);
                this.onTranscriptUpdateCb(transcriptPayload as MeetingTranscriptionItem);
              }
              break;
            default:
              // Unknown transcript event type - ignore for now
              break;
          }
        }
      } catch (err) {
        console.error('❌ Failed to parse WebSocket message:', err);
      }
    };
  }

  /**
   * Start ping/pong heartbeat to keep connection alive.
   * Sends JSON ping messages that the server will respond to with pong.
   */
  private startHeartbeat() {
    this.stopHeartbeat(); // Clear any existing interval

    this.pingInterval = setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        try {
          // Send JSON ping message
          this.socket.send(
            JSON.stringify({
              type: 'ping',
              timestamp: Date.now(),
            }),
          );
        } catch (error) {
          console.error('Error sending ping:', error);
        }
      } else {
        // Connection is not open, stop heartbeat
        this.stopHeartbeat();
      }
    }, this.PING_INTERVAL_MS);
  }

  /**
   * Stop ping/pong heartbeat.
   */
  private stopHeartbeat() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  disconnect() {
    this.stopHeartbeat();
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    this.lastToken = null;
  }
}

export const websocketService = new WebSocketService();
